import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Skeleton } from './ui/skeleton';
import { 
  BarChart3, 
  Download, 
  Shield, 
  Users, 
  TrendingUp, 
  Clock,
  AlertCircle,
  CheckCircle2,
  Lock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ResearchPortal - SSO-authenticated research data access
 * 
 * URL-based SSO (Option 2 - No API Calls):
 * 1. Researcher clicks link from belongingsessions.com
 * 2. URL contains: ?bs_user=XXX&bs_token=YYY&bs_time=ZZZ
 * 3. This component validates with backend and displays data
 */
export default function ResearchPortal() {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [researcher, setResearcher] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [researchData, setResearchData] = useState(null);
  const [correlations, setCorrelations] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Handle SSO authentication on mount
  useEffect(() => {
    handleSSOAuthentication();
  }, []);

  const handleSSOAuthentication = async () => {
    setIsAuthenticating(true);
    setAuthError(null);

    // Check for existing session
    const existingSession = localStorage.getItem('research_session_id');
    const existingResearcher = localStorage.getItem('research_user');
    
    if (existingSession && existingResearcher) {
      // Verify existing session is still valid
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/soul-score/research/secure?session_id=${existingSession}`
        );
        
        if (response.ok) {
          setSessionId(existingSession);
          setResearcher(JSON.parse(existingResearcher));
          setIsAuthenticating(false);
          return;
        } else {
          // Session expired, clear and continue
          localStorage.removeItem('research_session_id');
          localStorage.removeItem('research_user');
        }
      } catch (e) {
        console.log('Session verification failed, proceeding with SSO');
      }
    }

    // Check URL for SSO parameters
    const params = new URLSearchParams(window.location.search);
    const bs_user = params.get('bs_user');
    const bs_token = params.get('bs_token');
    const bs_time = params.get('bs_time');

    if (bs_user && bs_token && bs_time) {
      try {
        // Validate with backend
        const response = await fetch(`${BACKEND_URL}/api/soul-score/research/sso/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bs_user, bs_token, bs_time })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          // Store session
          setSessionId(data.session_id);
          setResearcher(data.user);
          localStorage.setItem('research_session_id', data.session_id);
          localStorage.setItem('research_user', JSON.stringify(data.user));

          // Clean URL
          window.history.replaceState({}, '', window.location.pathname);
          
          setIsAuthenticating(false);
          return;
        } else {
          setAuthError(data.detail || 'Authentication failed');
        }
      } catch (error) {
        setAuthError('Failed to connect to authentication server');
        console.error('SSO error:', error);
      }
    } else {
      setAuthError('No SSO credentials found. Please access this page via the link from belongingsessions.com');
    }

    setIsAuthenticating(false);
  };

  // Fetch research data
  const fetchResearchData = async () => {
    if (!sessionId) return;
    
    setIsLoadingData(true);
    try {
      // Fetch secure detailed data
      const secureResponse = await fetch(
        `${BACKEND_URL}/api/soul-score/research/secure?session_id=${sessionId}`
      );
      
      if (secureResponse.ok) {
        const secureData = await secureResponse.json();
        setResearchData(secureData);
      }

      // Fetch correlations (public endpoint)
      const correlationsResponse = await fetch(
        `${BACKEND_URL}/api/soul-score/research/correlations`
      );
      
      if (correlationsResponse.ok) {
        const correlationsData = await correlationsResponse.json();
        setCorrelations(correlationsData);
      }
    } catch (error) {
      console.error('Error fetching research data:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  // Load data when authenticated
  useEffect(() => {
    if (sessionId) {
      fetchResearchData();
    }
  }, [sessionId]);

  const handleLogout = () => {
    localStorage.removeItem('research_session_id');
    localStorage.removeItem('research_user');
    setSessionId(null);
    setResearcher(null);
    setResearchData(null);
    setAuthError('You have been logged out. Please use the SSO link to re-authenticate.');
  };

  const exportData = (format) => {
    if (!researchData?.data) return;

    if (format === 'json') {
      const blob = new Blob([JSON.stringify(researchData.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `soul_score_research_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    } else if (format === 'csv') {
      const headers = ['user_hash', 'session_hash', 'checkpoint', 'score', 'delta', 'timestamp'];
      const csvContent = [
        headers.join(','),
        ...researchData.data.map(row => 
          headers.map(h => `"${row[h] || ''}"`).join(',')
        )
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `soul_score_research_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    }
  };

  // Loading state
  if (isAuthenticating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border-white/20">
          <CardContent className="pt-8 text-center">
            <div className="animate-pulse">
              <Shield className="w-16 h-16 mx-auto text-purple-400 mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Authenticating...</h2>
              <p className="text-gray-300">Verifying your research credentials</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Auth error state
  if (authError && !researcher) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-white/10 backdrop-blur-lg border-white/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Lock className="w-6 h-6 text-red-400" />
              Authentication Required
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive" className="bg-red-900/50 border-red-500">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Access Denied</AlertTitle>
              <AlertDescription>{authError}</AlertDescription>
            </Alert>
            
            <div className="text-gray-300 space-y-2">
              <p>To access the Soul Score Research Portal, you need:</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>An account at belongingsessions.com</li>
                <li>Researcher privileges</li>
                <li>A valid SSO link (generated from belongingsessions.com)</li>
              </ul>
            </div>

            <div className="pt-4 border-t border-white/20">
              <p className="text-sm text-gray-400 mb-3">
                Contact the BelongingSessions team to request researcher access.
              </p>
              <Button
                variant="outline"
                className="w-full border-purple-400 text-purple-300 hover:bg-purple-500/20"
                onClick={() => window.location.href = 'https://belongingsessions.com'}
              >
                Go to BelongingSessions.com
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Authenticated view
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="bg-black/30 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-purple-400" />
            <div>
              <h1 className="text-xl font-bold text-white">Soul Score Research Portal</h1>
              <p className="text-xs text-purple-300">ElchmE Wellbeing Research</p>
            </div>
          </div>
          
          {researcher && (
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-white font-medium">{researcher.name}</p>
                <p className="text-xs text-gray-400">{researcher.email}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-gray-400 hover:text-white"
              >
                Logout
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Status Bar */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Status</p>
                  <p className="text-white font-semibold">Authenticated</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Users className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Participants</p>
                  <p className="text-white font-semibold">
                    {correlations?.correlations?.total_participants || '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Data Points</p>
                  <p className="text-white font-semibold">
                    {researchData?.total_records || correlations?.correlations?.total_datapoints || '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Last Updated</p>
                  <p className="text-white font-semibold text-sm">
                    {researchData?.generated_at ? 
                      new Date(researchData.generated_at).toLocaleString() : '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Data Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-white/10 border-white/20">
            <TabsTrigger value="overview" className="data-[state=active]:bg-purple-500">
              Overview
            </TabsTrigger>
            <TabsTrigger value="detailed" className="data-[state=active]:bg-purple-500">
              Detailed Data
            </TabsTrigger>
            <TabsTrigger value="export" className="data-[state=active]:bg-purple-500">
              Export
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Checkpoint Impact */}
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    Checkpoint Impact Analysis
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Average scores and improvement at each checkpoint
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingData ? (
                    <div className="space-y-3">
                      <Skeleton className="h-12 bg-white/10" />
                      <Skeleton className="h-12 bg-white/10" />
                      <Skeleton className="h-12 bg-white/10" />
                    </div>
                  ) : correlations?.correlations?.checkpoint_impact ? (
                    <div className="space-y-3">
                      {Object.entries(correlations.correlations.checkpoint_impact).map(([checkpoint, data]) => (
                        <div 
                          key={checkpoint}
                          className="p-3 bg-white/5 rounded-lg flex items-center justify-between"
                        >
                          <div>
                            <p className="text-white font-medium capitalize">{checkpoint}</p>
                            <p className="text-xs text-gray-400">
                              {data.participant_count} participants
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-white">{data.average_score}</p>
                            <Badge 
                              variant={data.average_improvement >= 0 ? "default" : "destructive"}
                              className={data.average_improvement >= 0 ? "bg-green-500/20 text-green-400" : ""}
                            >
                              {data.average_improvement >= 0 ? '+' : ''}{data.average_improvement}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-center py-8">No data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Methodology */}
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">Research Methodology</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-300 space-y-4">
                  <p>
                    <strong className="text-white">Soul Score</strong> is a subjective wellbeing 
                    measurement collected at four key checkpoints during ElchmE NVC practice sessions:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm">
                    <li><strong className="text-purple-300">timeZero</strong> - Baseline after initial meditation</li>
                    <li><strong className="text-blue-300">belonging</strong> - After conflict resolution exercise</li>
                    <li><strong className="text-green-300">vision</strong> - After Vision Quest BHAG phase</li>
                    <li><strong className="text-amber-300">story</strong> - After AI story generation</li>
                  </ul>
                  <p className="text-sm">
                    Users tap 1-10 times to indicate their current wellbeing level. Data is 
                    anonymized using hash-based user IDs to protect participant privacy.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Detailed Data Tab */}
          <TabsContent value="detailed">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Detailed Research Records</CardTitle>
                <CardDescription className="text-gray-400">
                  Anonymized individual data points
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingData ? (
                  <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-12 bg-white/10" />
                    ))}
                  </div>
                ) : researchData?.data?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10">
                          <TableHead className="text-gray-300">User Hash</TableHead>
                          <TableHead className="text-gray-300">Session</TableHead>
                          <TableHead className="text-gray-300">Checkpoint</TableHead>
                          <TableHead className="text-gray-300">Score</TableHead>
                          <TableHead className="text-gray-300">Delta</TableHead>
                          <TableHead className="text-gray-300">Timestamp</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {researchData.data.slice(0, 50).map((row, idx) => (
                          <TableRow key={idx} className="border-white/10">
                            <TableCell className="text-gray-300 font-mono text-xs">
                              {row.user_hash?.substring(0, 8)}...
                            </TableCell>
                            <TableCell className="text-gray-300 font-mono text-xs">
                              {row.session_hash?.substring(0, 6)}...
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="border-purple-400 text-purple-300">
                                {row.checkpoint}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-white font-bold">{row.score}</TableCell>
                            <TableCell>
                              <span className={row.delta >= 0 ? 'text-green-400' : 'text-red-400'}>
                                {row.delta >= 0 ? '+' : ''}{row.delta}
                              </span>
                            </TableCell>
                            <TableCell className="text-gray-400 text-xs">
                              {new Date(row.timestamp).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {researchData.data.length > 50 && (
                      <p className="text-center text-gray-400 mt-4 text-sm">
                        Showing 50 of {researchData.data.length} records. Export for full data.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-8">No detailed records available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Export Tab */}
          <TabsContent value="export">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Export Research Data
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Download anonymized data for external analysis
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button
                    onClick={() => exportData('json')}
                    className="bg-blue-600 hover:bg-blue-700 h-24 flex flex-col items-center justify-center gap-2"
                    disabled={!researchData?.data}
                  >
                    <Download className="w-6 h-6" />
                    <span>Export as JSON</span>
                    <span className="text-xs opacity-70">Structured data format</span>
                  </Button>
                  
                  <Button
                    onClick={() => exportData('csv')}
                    className="bg-green-600 hover:bg-green-700 h-24 flex flex-col items-center justify-center gap-2"
                    disabled={!researchData?.data}
                  >
                    <Download className="w-6 h-6" />
                    <span>Export as CSV</span>
                    <span className="text-xs opacity-70">For Excel/Sheets</span>
                  </Button>
                </div>

                <Alert className="bg-purple-900/30 border-purple-500">
                  <Shield className="h-4 w-4 text-purple-400" />
                  <AlertTitle className="text-purple-300">Privacy Note</AlertTitle>
                  <AlertDescription className="text-gray-300">
                    All exported data is anonymized. User IDs are hashed and no personally 
                    identifiable information is included.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-6 mt-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-400 text-sm">
          <p>Soul Score Research Portal • ElchmE Wellbeing Measurement System</p>
          <p className="mt-1">© 2024 BelongingSessions.com • All research data is anonymized</p>
        </div>
      </footer>
    </div>
  );
}
